import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthLayout from '@/components/common/AuthLayout';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { setCurrentWorkspace } from '@/features/workspace/workspaceSlice';
import { fetchUserWorkspaces } from '@/features/workspace/workspaceThunks';
import type { Workspace } from '@/features/workspace/types';

const firstNameColor = '#C0FFEA';

export default function SelectWorkspacePage() {
  const { t } = useTranslation('auth');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((s: RootState) => s.auth.user);
  const workspacesList = useAppSelector((s: RootState) => s.workspace.workspacesList);
  const loading = useAppSelector((s: RootState) => s.workspace.loading);

  useEffect(() => {
    if (!workspacesList && !loading) {
      dispatch(fetchUserWorkspaces({ page: 0 }));
    }
  }, [dispatch, workspacesList, loading]);

  useEffect(() => {
    if (workspacesList?.length === 1) {
      navigate('/', { replace: true });
    }
  }, [workspacesList, navigate]);

  const handleSelect = (workspace: Workspace) => {
    dispatch(setCurrentWorkspace({ ...workspace, socials: workspace.socials ?? [] }));
    navigate('/', { replace: true });
  };

  const list = workspacesList ?? [];

  if (workspacesList?.length === 1) {
    return null;
  }

  return (
    <AuthLayout
      title={
        <div>
          {t('selectWorkspace.greetingPrefix')}{' '}
          <span style={{ color: firstNameColor }}>{user?.firstName}</span>
          {t('selectWorkspace.greetingSuffix')}
        </div>
      }
      background="gradient"
    >
      <div className="space-y-4">
        <p>{t('selectWorkspace.selectPrompt')}</p>
        <div
          className="space-y-6 max-h-[60vh] overflow-y-auto pr-1"
          style={{ scrollbarColor: 'currentColor transparent', scrollbarWidth: 'thin' }}
        >
          {loading && list.length === 0 ? (
            <p className="text-sm text-white/80">{t('selectWorkspace.loading')}</p>
          ) : list.length === 0 ? (
            <p className="text-sm text-white/80">{t('selectWorkspace.noWorkspace')}</p>
          ) : (
            list.map((workspace) => (
              <button
                key={workspace.id}
                type="button"
                onClick={() => handleSelect(workspace)}
                className="cursor-pointer flex w-full items-center p-4 gap-3 rounded-[20px]  bg-white/10 text-left text-white transition hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/30"
              >
                <div className="" />
                <div className="size-12 shrink-0 rounded-full bg-gradient-brand" aria-hidden />
                <div className="flex items-center gap-1">
                  <p className="text-xl font-medium">{workspace.name}</p>{' '}
                  <span className="text-sm text-tertiary-inverse">
                    ({t('selectWorkspace.roleAdmin')})
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </AuthLayout>
  );
}
